import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { alpha } from "@mui/material/styles";
import { groupVisitCardAddressesByCity } from "../../utils/visitCardAccess";
import {
  getPublicVisitCard,
  getPublicVisitCards,
  visitCardAssetUrl,
} from "../../api/User/visitCardsApi";
import { visitCardLogo } from "../../constants/visitCardLogos";

function initials(name = "") {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function telHref(value) {
  const digits = String(value || "").replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : "";
}

function waHref(value) {
  const digits = String(value || "").replace(/\D/g, "");
  return digits ? `https://wa.me/${digits}` : "";
}

function mapsHref(addr = {}) {
  if (addr.mapsUrl) return addr.mapsUrl;
  const q = [addr.street, addr.zip, addr.city, addr.country]
    .filter(Boolean)
    .join(", ");
  return q ? `https://maps.google.com/?q=${encodeURIComponent(q)}` : "";
}

function addressResponsible(addr = {}) {
  return String(addr.responsible || addr.Responsible || "").trim();
}

function formatAddr(addr = {}, { omitCity = false } = {}) {
  return [
    addr.street,
    omitCity ? addr.zip : [addr.zip, addr.city].filter(Boolean).join(" "),
    addr.region,
    addr.country,
  ]
    .filter(Boolean)
    .join(", ");
}

function isLightHex(hex) {
  const h = String(hex || "").replace("#", "");
  if (h.length < 6) return true;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (r * 299 + g * 587 + b * 114) / 1000 > 155;
}

function ContactRow({ href, icon, label, value, onClick, accent }) {
  if (!value && !href && !onClick) return null;
  const interactive = Boolean(href || onClick);
  return (
    <Box
      component={href ? "a" : onClick ? "button" : "div"}
      href={href || undefined}
      onClick={onClick}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href ? "noreferrer" : undefined}
      aria-label={label}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.15,
        width: "100%",
        textAlign: "left",
        textDecoration: "none",
        color: "inherit",
        border: "none",
        cursor: interactive ? "pointer" : "default",
        bgcolor: "rgba(255,255,255,0.045)",
        borderRadius: 2.5,
        px: 1.3,
        py: 1,
        transition: "background 0.2s ease",
        "&:hover": interactive ? { bgcolor: "rgba(255,255,255,0.08)" } : undefined,
      }}
    >
      <Box sx={{ color: accent, display: "grid", placeItems: "center", flexShrink: 0 }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: "0.62rem",
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: "rgba(255,255,255,0.42)",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
        {value ? (
          <Typography
            sx={{
              mt: 0.2,
              fontSize: "0.9rem",
              fontWeight: 700,
              lineHeight: 1.25,
              wordBreak: "break-word",
            }}
          >
            {value}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function QuickAction({ href, icon, label, onClick }) {
  if (!href && !onClick) return null;
  return (
    <Button
      href={href || undefined}
      onClick={onClick}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href ? "noreferrer" : undefined}
      sx={{
        flex: 1,
        minWidth: 0,
        flexDirection: "column",
        gap: 0.35,
        py: 0.95,
        px: 0.4,
        borderRadius: 2.5,
        textTransform: "none",
        color: "#fff",
        bgcolor: "rgba(255,255,255,0.045)",
        "&:hover": { bgcolor: "rgba(255,255,255,0.09)" },
      }}
    >
      {icon}
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700 }}>
        {label}
      </Typography>
    </Button>
  );
}

function VisitCardView({ card }) {
  const { t } = useTranslation();
  const accent = card.company?.brandColor || "#00c2a3";
  const photo = visitCardAssetUrl(card.photoUrl);
  const qr = visitCardAssetUrl(card.qrUrl);
  const vcf = visitCardAssetUrl(card.vcfUrl);
  const addrs = card.company?.addresses || [];
  const logo = visitCardLogo(card.company?.logoKey, card.company);
  const phone = card.mobile || card.phone || "";
  const [wechatCopied, setWechatCopied] = useState(false);
  const [cityIdx, setCityIdx] = useState(0);
  const ctaColor = isLightHex(accent) ? "#08110f" : "#fff";
  const cityGroups = useMemo(
    () => groupVisitCardAddressesByCity(addrs),
    [addrs],
  );
  const activeCity = cityGroups[Math.min(cityIdx, Math.max(cityGroups.length - 1, 0))] || null;

  const share = async () => {
    const url = card.publicUrl || window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: card.fullName,
          text: `${card.fullName} · ${card.company?.name || ""}`,
          url,
        });
        return;
      } catch {
        /* cancelled */
      }
    }
    await navigator.clipboard.writeText(url);
  };

  const copyWechat = async () => {
    if (!card.wechat) return;
    try {
      await navigator.clipboard.writeText(card.wechat);
      setWechatCopied(true);
      window.setTimeout(() => setWechatCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 420,
        borderRadius: 6,
        overflow: "hidden",
        bgcolor: "#121615",
        color: "#f4f7f6",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: `0 24px 80px ${alpha("#000", 0.45)}, 0 0 0 1px ${alpha(accent, 0.12)}`,
      }}
    >
      <Box
        sx={{
          height: 112,
          position: "relative",
          background: `radial-gradient(90% 140% at 0% -10%, ${accent} 0%, ${alpha(accent, 0.2)} 42%, #101413 78%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, transparent 42%, rgba(0,0,0,0.28) 100%)",
          }}
        />
        <Box
          component="img"
          src={logo.src}
          alt=""
          sx={{
            position: "absolute",
            right: 18,
            top: 18,
            width: logo.wide ? 124 : 46,
            height: logo.wide ? 40 : 46,
            objectFit: "contain",
            bgcolor: logo.lightBg ? "#fff" : "rgba(8,12,11,0.35)",
            p: logo.lightBg ? 0.6 : 0.7,
            borderRadius: 2,
            backdropFilter: "blur(8px)",
            boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
          }}
        />
      </Box>
      <Box sx={{ px: 2.5, pb: 2.5, mt: -5.5 }}>
        <Avatar
          src={photo || undefined}
          alt={card.fullName}
          sx={{
            width: 88,
            height: 88,
            fontSize: 28,
            fontWeight: 800,
            bgcolor: accent,
            border: "4px solid #121615",
            mb: 1.5,
            boxShadow: `0 10px 24px ${alpha(accent, 0.28)}`,
          }}
        >
          {initials(card.fullName)}
        </Avatar>
        <Typography sx={{ fontWeight: 800, fontSize: "1.55rem", lineHeight: 1.12, letterSpacing: -0.3 }}>
          {card.fullName}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,0.62)", fontWeight: 600, mt: 0.45, fontSize: "0.92rem" }}>
          {card.roleTitle}
        </Typography>
        <Typography
          sx={{
            color: accent,
            fontWeight: 800,
            letterSpacing: 0.45,
            mt: 0.55,
            fontSize: "0.76rem",
            textTransform: "uppercase",
          }}
        >
          {card.company?.name}
        </Typography>
        {card.bio ? (
          <Typography sx={{ mt: 1.3, color: "rgba(255,255,255,0.62)", fontSize: "0.88rem", lineHeight: 1.45 }}>
            {card.bio}
          </Typography>
        ) : null}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 0.85,
            mt: 2.2,
          }}
        >
          <ContactRow
            href={telHref(phone)}
            icon={<PhoneOutlinedIcon fontSize="small" />}
            label={t("visitCards.phone")}
            value={phone}
            accent={accent}
          />
          <ContactRow
            icon={<ChatBubbleOutlineOutlinedIcon fontSize="small" />}
            label={wechatCopied ? t("visitCards.copied") : t("visitCards.wechat")}
            value={card.wechat}
            onClick={card.wechat ? copyWechat : undefined}
            accent={accent}
          />
          <Box sx={{ gridColumn: "1 / -1" }}>
            <ContactRow
              href={card.email ? `mailto:${card.email}` : ""}
              icon={<EmailOutlinedIcon fontSize="small" />}
              label={t("visitCards.email")}
              value={card.email}
              accent={accent}
            />
          </Box>
        </Box>

        <Stack direction="row" spacing={0.85} mt={0.85}>
          <QuickAction
            href={waHref(card.whatsapp || card.mobile)}
            icon={<ChatOutlinedIcon fontSize="small" />}
            label={t("visitCards.whatsapp")}
          />
          <QuickAction href={card.linkedin} icon={<LinkedInIcon fontSize="small" />} label={t("visitCards.linkedin")} />
          <QuickAction
            href={card.website || card.company?.website}
            icon={<LanguageOutlinedIcon fontSize="small" />}
            label={t("visitCards.web")}
          />
        </Stack>

        {cityGroups.length ? (
          <Box sx={{ mt: 2.2 }}>
            {cityGroups.length > 1 ? (
              <Stack direction="row" flexWrap="wrap" gap={0.7} mb={1}>
                {cityGroups.map((group, idx) => {
                  const selected = idx === cityIdx;
                  return (
                    <Chip
                      key={group.city}
                      label={group.city}
                      onClick={() => setCityIdx(idx)}
                      sx={{
                        height: 28,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        color: selected ? ctaColor : "rgba(255,255,255,0.78)",
                        bgcolor: selected ? accent : "rgba(255,255,255,0.06)",
                        "&:hover": {
                          bgcolor: selected ? accent : "rgba(255,255,255,0.12)",
                        },
                      }}
                    />
                  );
                })}
              </Stack>
            ) : null}
            {activeCity ? (
              <Box
                sx={{
                  p: 1.45,
                  borderRadius: 2.5,
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <Box sx={{ display: "flex", gap: 1.2 }}>
                  <PlaceOutlinedIcon sx={{ color: accent, mt: 0.15, fontSize: 20 }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: "0.92rem" }}>
                      {activeCity.city}
                    </Typography>
                    <Stack spacing={0.85} mt={0.45}>
                      {activeCity.items.map((addr, idx) => {
                        const href = mapsHref(addr);
                        const extraLabel =
                          addr.label &&
                          addr.label.toLowerCase() !== activeCity.city.toLowerCase()
                            ? addr.label
                            : "";
                        const line = formatAddr(addr, { omitCity: true });
                        const responsible = addressResponsible(addr);
                        return (
                          <Box key={addr.id || `${activeCity.city}-${idx}`}>
                            <Box
                              component={href ? "a" : "div"}
                              href={href || undefined}
                              target={href ? "_blank" : undefined}
                              rel="noreferrer"
                              sx={{ textDecoration: "none", color: "inherit" }}
                            >
                              {extraLabel ? (
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: "0.72rem",
                                    color: "rgba(255,255,255,0.48)",
                                    mb: 0.15,
                                  }}
                                >
                                  {extraLabel}
                                </Typography>
                              ) : null}
                              {line ? (
                                <Typography
                                  sx={{
                                    color: "rgba(255,255,255,0.68)",
                                    fontSize: "0.84rem",
                                    lineHeight: 1.4,
                                  }}
                                >
                                  {line}
                                </Typography>
                              ) : null}
                            </Box>
                            {responsible ? (
                              <Box sx={{ mt: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: "0.62rem",
                                    fontWeight: 700,
                                    letterSpacing: 0.5,
                                    textTransform: "uppercase",
                                    color: "rgba(255,255,255,0.42)",
                                    lineHeight: 1,
                                  }}
                                >
                                  {t("visitCards.responsible")}
                                </Typography>
                                <Typography
                                  sx={{
                                    mt: 0.35,
                                    fontSize: "0.9rem",
                                    fontWeight: 700,
                                    lineHeight: 1.25,
                                    color: "#fff",
                                  }}
                                >
                                  {responsible}
                                </Typography>
                              </Box>
                            ) : null}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Box>
                </Box>
              </Box>
            ) : null}
          </Box>
        ) : null}

        <Stack direction="row" spacing={1} mt={2.2}>
          <Button
            fullWidth
            href={vcf}
            startIcon={<PersonAddAltOutlinedIcon />}
            sx={{
              py: 1.2,
              borderRadius: 2.5,
              fontWeight: 800,
              textTransform: "none",
              bgcolor: accent,
              color: ctaColor,
              boxShadow: `0 8px 22px ${alpha(accent, 0.24)}`,
              "&:hover": { bgcolor: accent, filter: "brightness(1.08)" },
            }}
          >
            {t("visitCards.saveContact")}
          </Button>
          <IconButton
            onClick={share}
            sx={{
              width: 48,
              border: "1px solid rgba(255,255,255,0.1)",
              color: "#fff",
              borderRadius: 2.5,
              bgcolor: "rgba(255,255,255,0.04)",
            }}
          >
            <IosShareOutlinedIcon />
          </IconButton>
        </Stack>

        {qr ? (
          <Box
            sx={{
              mt: 1.5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={qr}
              alt="QR"
              sx={{
                display: "block",
                width: 120,
                height: 120,
                borderRadius: 2,
                bgcolor: "#fff",
                p: 0.8,
              }}
            />
            <Button
              href={qr}
              download
              startIcon={<QrCode2OutlinedIcon />}
              sx={{ mt: 1, color: "rgba(255,255,255,0.7)", textTransform: "none" }}
            >
              {t("visitCards.downloadQr")}
            </Button>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}

const CARD_LANGS = [
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
  { code: "ch", label: "中文" },
  { code: "dk", label: "DA" },
  { code: "nl", label: "NL" },
];

function changeVisitCardLanguage(i18n, lng) {
  i18n.changeLanguage(lng);
  localStorage.setItem("lng", lng);
  localStorage.setItem("i18nextLng", lng);
}

const VisitCardPublicPage = () => {
  const { t, i18n } = useTranslation();
  const { userSlug, cardSlug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [picker, setPicker] = useState(null);
  const [card, setCard] = useState(null);

  const title = useMemo(() => {
    if (card?.fullName) return `${card.fullName} · ${card.company?.name || "Stage"}`;
    return t("visitCards.pageTitle");
  }, [card, t]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setCard(null);
      setPicker(null);
      try {
        if (cardSlug) {
          const data = await getPublicVisitCard(userSlug, cardSlug);
          if (!cancelled) setCard(data);
          return;
        }
        const list = await getPublicVisitCards(userSlug);
        if (cancelled) return;
        if ((list.cards || []).length === 1) {
          const only = list.cards[0];
          const data = await getPublicVisitCard(only.userSlug, only.cardSlug);
          if (!cancelled) setCard(data);
        } else {
          setPicker(list);
        }
      } catch {
        if (!cancelled) setError("missing");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [userSlug, cardSlug]);

  const currentLang =
    CARD_LANGS.find((lang) => i18n.language?.startsWith(lang.code))?.code || "en";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#070a09",
        background: `
          radial-gradient(80% 50% at 50% -10%, ${alpha("#00c2a3", 0.16)} 0%, transparent 58%),
          #070a09
        `,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        py: { xs: 3, md: 6 },
      }}
    >
      <Stack direction="row" spacing={0.6} mb={2}>
        {CARD_LANGS.map((lang) => {
          const selected = currentLang === lang.code;
          return (
            <Chip
              key={lang.code}
              label={lang.label}
              onClick={() => changeVisitCardLanguage(i18n, lang.code)}
              sx={{
                height: 26,
                fontWeight: 800,
                fontSize: "0.7rem",
                color: selected ? "#08110f" : "rgba(255,255,255,0.72)",
                bgcolor: selected ? "#00c2a3" : "rgba(255,255,255,0.08)",
                "&:hover": {
                  bgcolor: selected ? "#00c2a3" : "rgba(255,255,255,0.16)",
                },
              }}
            />
          );
        })}
      </Stack>
      {loading ? (
        <CircularProgress sx={{ color: "#00c2a3", mt: 12 }} />
      ) : error ? (
        <Alert severity="info" sx={{ height: "fit-content", mt: 8 }}>
          {t("visitCards.notFound")}
        </Alert>
      ) : card ? (
        <VisitCardView card={card} />
      ) : picker ? (
        <Box sx={{ width: "100%", maxWidth: 420 }}>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: "1.5rem", mb: 0.6, letterSpacing: -0.3 }}>
            {picker.cards[0]?.fullName || userSlug}
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,0.55)", mb: 3 }}>
            {t("visitCards.chooseCompany")}
          </Typography>
          <Stack spacing={1.3}>
            {picker.cards.map((item) => (
              <Box
                key={item.cardSlug}
                component={Link}
                to={`/c/${item.userSlug}/${item.cardSlug}`}
                sx={{
                  display: "block",
                  textDecoration: "none",
                  color: "#fff",
                  p: 2.2,
                  borderRadius: 3.5,
                  bgcolor: "#121615",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderLeft: `5px solid ${item.brandColor || "#00c2a3"}`,
                  transition: "transform 0.2s ease, background 0.2s ease",
                  "&:hover": { transform: "translateY(-2px)", bgcolor: "#171c1b" },
                }}
              >
                <Typography sx={{ fontWeight: 800 }}>{item.companyName}</Typography>
                <Typography sx={{ color: "rgba(255,255,255,0.65)", fontSize: "0.9rem" }}>
                  {item.roleTitle}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      ) : null}
    </Box>
  );
};

export default VisitCardPublicPage;
